import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

const DURATION = { success: 3500, error: 5000, warning: 4000, info: 3500 };

// Raw color values so we can compose tints inline
const TYPE = {
  success: { rgb: '74,222,128',   color: 'var(--success)', iconBg: 'rgba(74,222,128,0.15)',  border: 'rgba(74,222,128,0.32)',  bar: 'rgba(74,222,128,0.9)',  tint: 'rgba(74,222,128,0.07)'  },
  error:   { rgb: '255,107,129',  color: 'var(--danger)',  iconBg: 'rgba(255,107,129,0.14)', border: 'rgba(255,107,129,0.32)', bar: 'rgba(255,107,129,0.9)', tint: 'rgba(255,107,129,0.07)' },
  warning: { rgb: '251,191,36',   color: 'var(--warning)', iconBg: 'rgba(251,191,36,0.14)',  border: 'rgba(251,191,36,0.30)',  bar: 'rgba(251,191,36,0.9)',  tint: 'rgba(251,191,36,0.06)'  },
  info:    { rgb: '124,106,245',  color: 'var(--accent)',  iconBg: 'rgba(124,106,245,0.15)', border: 'rgba(124,106,245,0.30)', bar: 'rgba(124,106,245,0.9)', tint: 'rgba(124,106,245,0.07)' },
};

const IcoCheck = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2.5 8.5 6.5 12.5 13.5 4.5" />
  </svg>
);
const IcoX = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="3.5" y1="3.5" x2="12.5" y2="12.5" /><line x1="12.5" y1="3.5" x2="3.5" y2="12.5" />
  </svg>
);
const IcoWarn = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2.5L14 13H2Z" /><line x1="8" y1="7" x2="8" y2="10" /><circle cx="8" cy="11.8" r="0.7" fill="currentColor" strokeWidth="0" />
  </svg>
);
const IcoInfo = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <circle cx="8" cy="8" r="5.5" /><line x1="8" y1="7.5" x2="8" y2="11" /><circle cx="8" cy="5.5" r="0.55" fill="currentColor" strokeWidth="0" />
  </svg>
);
const IcoDismiss = () => (
  <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="2" y1="2" x2="12" y2="12" /><line x1="12" y1="2" x2="2" y2="12" />
  </svg>
);

const ICONS = { success: IcoCheck, error: IcoX, warning: IcoWarn, info: IcoInfo };

let injected = false;
function injectStyles() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes _t-in  { from { opacity:0; transform:translateY(10px) scale(0.96); } to { opacity:1; transform:translateY(0) scale(1); } }
    @keyframes _t-out { from { opacity:1; transform:translateX(0)    scale(1);    } to { opacity:0; transform:translateX(20px) scale(0.97); } }
    @keyframes _t-bar { from { transform:scaleX(1); } to { transform:scaleX(0); } }
  `;
  document.head.appendChild(s);
}

function ToastItem({ t, onRemove }) {
  const cfg = TYPE[t.type] || TYPE.info;
  const Icon = ICONS[t.type] || IcoInfo;
  const duration = DURATION[t.type] ?? 3500;

  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startRef = useRef(null);

  const dismiss = useCallback(() => {
    setExiting(true);
    clearTimeout(timerRef.current);
    setTimeout(() => onRemove(t.id), 240);
  }, [t.id, onRemove]);

  const startTimer = useCallback(() => {
    startRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss]);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(timerRef.current);
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - (startRef.current ?? Date.now())));
    setPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPaused(false);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    startTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTimer]);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: 300,
        padding: '10px 12px 12px 14px',
        borderRadius: 11,
        background: `linear-gradient(135deg, ${cfg.tint} 0%, var(--glass-xl-bg, rgba(9,11,20,0.94)) 55%)`,
        border: `1px solid ${cfg.border}`,
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        boxShadow: `0 8px 28px rgba(0,0,0,0.22), 0 0 0 0.5px rgba(${cfg.rgb},0.12) inset`,
        position: 'relative',
        overflow: 'hidden',
        pointerEvents: 'auto',
        animation: exiting
          ? '_t-out 0.24s cubic-bezier(.4,0,1,1) forwards'
          : '_t-in 0.24s cubic-bezier(0,0,.2,1)',
      }}
    >
      {/* Left stripe */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
        background: cfg.color, borderRadius: '11px 0 0 11px',
        opacity: 0.85,
      }} />

      {/* Icon bubble */}
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0, marginLeft: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: cfg.iconBg, color: cfg.color,
      }}>
        <Icon />
      </div>

      {/* Message */}
      <span style={{
        flex: 1, fontSize: 12.5, fontWeight: 500, lineHeight: 1.45,
        color: 'var(--text)', letterSpacing: '-0.01em',
      }}>
        {t.message}
      </span>

      {/* Dismiss */}
      <button
        onClick={dismiss}
        style={{
          background: 'none', border: 'none', padding: 4, cursor: 'pointer',
          color: 'var(--muted)', borderRadius: 5, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          transition: 'color 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--hover-bg)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.background = 'none'; }}
      >
        <IcoDismiss />
      </button>

      {/* Progress bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `rgba(${cfg.rgb},0.12)` }}>
        <div style={{
          height: '100%',
          background: cfg.bar,
          transformOrigin: 'left',
          animation: `_t-bar ${duration}ms linear forwards`,
          animationPlayState: paused ? 'paused' : 'running',
        }} />
      </div>
    </div>
  );
}

function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    injectStyles();
    const handler = (e) => {
      setToasts((prev) => {
        const next = [...prev, e.detail];
        return next.length > 5 ? next.slice(next.length - 5) : next;
      });
    };
    window.addEventListener('eclipse:toast', handler);
    return () => window.removeEventListener('eclipse:toast', handler);
  }, []);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (typeof document === 'undefined' || toasts.length === 0) return null;

  return createPortal(
    <div style={{
      position: 'fixed', bottom: 22, right: 22, zIndex: 99999,
      display: 'flex', flexDirection: 'column', gap: 7,
      alignItems: 'flex-end', pointerEvents: 'none',
    }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onRemove={remove} />)}
    </div>,
    document.body
  );
}

export default ToastContainer;
